<template>
  <div class="d-flex flex-column justify-content-center">
    <div class="fre-login-wrapper success clearfix">
      <b-form
        id="login-form"
        key="login-form"
        ref="login-form"
        @submit.prevent="onSubmit">
        <h2 class="sr-only">Login form</h2>
        <div class="illustration">
          <router-link to="/">
            <img
              alt="FRE logo"
              src="../assets/logo.png" />
          </router-link>
        </div>
        <div class="d-flex flex-row justify-content-center align-items-center pb-5 pt-2">
          <span class="text-white pr-2">{{ $t('LNG_SIGN_UP_TEXT') }}</span>
          <router-link
            to="/signup"
            class="k-button k-button-md k-rounded-md k-button-flat k-button-flat-base text-white text-right"
            >{{ $t('LNG_SIGN_UP') }}</router-link
          >
        </div>
        <div class="d-flex flex-row justify-content-center">
          <div class="d-flex flex-column w-75">
            <b-form-group
              id="lf-username-group"
              key="lf-username-group">
              <b-form-input
                id="lf-username"
                key="lf-username"
                ref="lfUsername"
                v-model="lfData.username"
                v-validate="{ required: true, email: true }"
                name="lf-username"
                type="email"
                :data-vv-as="$t('LNG_USERNAME')"
                :placeholder="$t('LNG_USERNAME_PLACEHOLDER')"
                :state="validateState('lf-username')">
              </b-form-input>
              <b-form-invalid-feedback id="lf-username-feedback">
                {{ errors.first('lf-username') }}
              </b-form-invalid-feedback>
            </b-form-group>
            <b-form-group
              id="lf-password-group"
              key="lf-password-group">
              <b-input-group>
                <b-form-input
                  id="lf-password"
                  key="lf-password"
                  ref="lfPassword"
                  v-model="lfData.password"
                  v-validate="{ required: true }"
                  name="lf-password"
                  :type="showPassword ? 'text' : 'password'"
                  :data-vv-as="$t('LNG_PASSWORD')"
                  :placeholder="$t('LNG_PASSWORD_PLACEHOLDER')"
                  :state="validateState('lf-password')"
                  autocomplete="off"
                  @keyup="isCapsLockOn">
                </b-form-input>
                <b-input-group-append>
                  <b-button
                    variant="secondary"
                    @click="showPassword = !showPassword">
                    <i
                      v-if="!showPassword"
                      :class="['fas', 'fa-eye']"></i>
                    <i
                      v-else
                      :class="['fas', 'fa-eye-slash']"></i>
                  </b-button>
                </b-input-group-append>
                <b-form-invalid-feedback id="lf-password-feedback">
                  {{ errors.first('lf-password') }}
                </b-form-invalid-feedback>
              </b-input-group>
            </b-form-group>
            <div class="d-flex flex-row justify-content-between align-items-center">
              <b-form-checkbox
                id="lf-rememberMe"
                key="lf-rememberMe"
                ref="lfRememberMe"
                v-model="lfData.rememberMe"
                name="lf-rememberMe"
                class="text-white">
                {{ $t('LNG_REMEMBER_ME') }}
              </b-form-checkbox>
              <router-link
                to="/reset"
                class="k-button k-button-md k-rounded-md k-button-flat k-button-flat-base text-white text-right">
                {{ $t('LNG_PASSWORD_RECOVERY') }}
              </router-link>
            </div>
            <div class="d-flex flex-row pt-3">
              <b-button
                type="submit"
                block
                variant="primary"
                class="my-2 my-sm-0 is-inverse">
                <div v-if="!isLoging">
                  {{ $t('LNG_LOGIN') }}
                </div>
                <div v-else>
                  <b-spinner
                    small
                    type="grow"></b-spinner>
                  {{ $t('LNG_ONGOING_LOGIN') }}
                </div>
              </b-button>
            </div>
          </div>
        </div>
      </b-form>
      <b-dropdown
        dropup
        no-caret
        variant="primary"
        class="float-right btn-float">
        <template #button-content><i class="fas fa-language fa-lg"></i></template>
        <b-dropdown-item-button
          v-for="(language, key) in myLanguages"
          :key="`lang-${key}`"
          @click.prevent="changeLanguage(language.value)">
          {{ language.text }}
        </b-dropdown-item-button>
      </b-dropdown>
      <div style="clear: both"></div>
    </div>
    <b-alert
      :show="isCapsLockNotification"
      variant="warning">
      {{ $t('LNG_CAPS_LOCK_WARNING') }}
    </b-alert>
  </div>
</template>
<script>
import { mapState, mapActions } from 'vuex';
import languages from '../locales/languages';

export default {
  name: 'LoginView',
  data() {
    return {
      myLanguages: languages,
      myLanguage: '',
      lfData: {},
      showPassword: false,
      isCapsLockNotification: false,
      isLoging: false,
    };
  },
  computed: {
    ...mapState(['lang']),
  },
  mounted() {
    this.$nextTick(() => this.$refs.lfUsername.focus());
  },
  methods: {
    ...mapActions([
      'authenticate',
      'deauthenticate',
      'changeLanguage',
      'setJobs',
      'setChips',
      'updateMe',
      'setReleases',
      'setWizard',
    ]),
    isCapsLockOn(e) {
      if (e.getModifierState && e.getModifierState('CapsLock')) {
        if (!this.isCapsLockNotification) {
          this.isCapsLockNotification = true;
        }
      } else {
        this.isCapsLockNotification = false;
      }
    },
    validateState(ref) {
      if (this.veeFields[ref] && (this.veeFields[ref].dirty || this.veeFields[ref].validated)) {
        return !this.errors.has(ref);
      }
      return null;
    },
    onSubmit() {
      this.isLoging = true;
      this.lfData.username = this.lfData.username.toLowerCase();
      this.$validator.validateAll().then((valid) => {
        if (valid) {
          const isRememberMe = this.lfData.rememberMe;
          const reqData = {
            username: this.lfData.username,
            password: this.lfData.password,
            lang: this.lang,
          };

          if (isRememberMe) {
            reqData.autologinId = this.$sha256(
              navigator.userAgent + reqData.username + new Date().getTime(),
            ).toString();
          }

          this.axios({
            method: 'POST',
            url: '/auth/prelogin',
            data: { username: reqData.username },
          })
            .then(async (rspap) => {
              const token = rspap.data.data
                ? this.$sha256(rspap.data.data.token1 + reqData.username).toString()
                : '';
              reqData.token = token;

              await this.axios({
                method: 'POST',
                url: '/auth/login',
                data: reqData,
              })
                .then((rspal) => {
                  const rspData = rspal.data;
                  if (isRememberMe && rspData.data.autologinToken) {
                    localStorage.setItem('alt', rspData.data.autologinToken);
                    localStorage.setItem('ali', reqData.autologinId);
                  }

                  if (this.lang !== rspData.data.lang) {
                    this.changeLanguage(rspData.data.lang);
                  }

                  // Login successful
                  const user = {
                    id: rspData.data.id,
                    username: reqData.username,
                    roles: rspData.data.roles,
                    name: rspData.data.name,
                    firstname: rspData.data.firstname,
                    licenceDidactique: rspData.data.licence_didactique,
                    licenceFinance: rspData.data.licence_finance,
                    licenceStatistiques: rspData.data.licence_statistiques,
                    licenceAdmin: rspData.data.licence_administrative,
                    address: rspData.data.address,
                    city: rspData.data.city,
                    canCreateCourse: rspData.data.canCreateCourse,
                    canCreateSession: rspData.data.canCreateSession,
                    canSendToL2: rspData.data.canSendToL2,
                    trainingCardLevel: rspData.data.training_card_level,
                    logo: rspData.data.logo,
                    tcs: rspData.data.tc,
                    starredResources: rspData.data.has_favorite_resource,
                    startWorkTime: rspData.data.startWorkTime,
                    endWorkTime: rspData.data.endWorkTime,
                  };

                  const releases = rspData.data.release;
                  const wizard = rspData.data.assistant ? rspData.data.assistant : false;

                  this.authenticate({
                    user: user,
                  });

                  if (this.$hasOnlyRole('user')) {
                    this.updateMe({
                      monitors: rspData.data.monitors,
                    });
                  }

                  if (this.$hasRole('monitor')) {
                    this.updateMe({
                      requests: rspData.data.requests,
                    });

                    this.setReleases(releases);
                    this.setWizard(wizard);
                  }

                  this.loadJobs();
                  if (this.$hasRole('fre')) {
                    rspData.data.login_buttons.push('addMonitor');
                  }
                  this.setChips(rspData.data.login_buttons);

                  if (rspData.data.name === '' || rspData.data.firstname === '') {
                    this.$router.push({ path: '/account/me' });
                  } else if (this.$route.query.redirect) {
                    this.$router.push(this.$route.query.redirect);
                  } else if (this.$hasOnlyRole('user')) {
                    this.$router.push({ path: '/dashboard' });
                  } else {
                    this.$router.push({ path: '/' });
                  }
                })
                .catch((error) => {
                  const rspsc = error.response.status;
                  switch (rspsc) {
                    case 401:
                      this.$root.$emit('notification::show', {
                        message: error.response.data.message,
                        type: 'danger',
                      });
                      this.deauthenticate();
                      break;
                    default:
                      break;
                  }
                })
                .finally(() => {});
            })
            .catch((error) => {
              const rspsc = error.response.status;
              switch (rspsc) {
                case 401:
                  this.$root.$emit('notification::show', {
                    message: error.response.data.message,
                    type: 'danger',
                  });
                  this.deauthenticate();
                  break;
                default:
                  break;
              }
            })
            .finally(() => {
              this.isLoging = false;
            });
        }
      });
    },
    loadJobs() {
      this.axios
        .get('/job/list')
        .then((response) => {
          const jobs = response.data.data;
          this.setJobs(jobs);
        })
        .catch((error) => {
          this.$root.$emit('notification::show', {
            message:
              typeof error.response === 'undefined'
                ? this.$t('LNG_UNKOWN_FATAL_ERROR')
                : error.response.data.message,
            type: 'danger',
          });
        });
    },
  },
};
</script>
